<template>
  <div :id="$attrs['id']" :class="[[$attrs['class'], gridClasses], {'hm-range--active': internalValue}, {'hm-range--disabled': disabledCombined}]" class="hm-range">
    <div class="hm-range__wrapper">
      <div class="hm-range__label"><span class="icon"><i v-if="faIcon" :class="`${faIcon}`" style="padding-right:0.5rem;"></i></span> {{ label }}</div>
      <div class="hm-range__range">
        <div :style="rangeStyles" :class="[{'range-slider--disabled': disabledCombined}]" data-ticks-position="top" class="range-slider">
          <input :min="min"
                 :max="max"
                 :step="step"
                 v-model="internalValue"
                 :disabled="disabledCombined"
                 type="range"
                 @input="onInput">
          <output></output>
          <div class="range-slider__progress"></div>
        </div>
      </div>
    </div>
    <div :title="help" class="hm-range__help" v-html="help" />
  </div>
</template>

<script>

import HmInputMixin from '@/mixins/HmInputMixin';
import GridMixin from '@/mixins/GridMixin';

export default {
  name: 'HmInputRange',

  mixins: [HmInputMixin, GridMixin],

  props: {
    faIcon: {
      type: String,
      default: null,
    },
    min: {
      type: Number,
      default: 0,
      required: true,
    },
    max: {
      type: Number,
      default: 10,
      required: true,
    },
    step: {
      type: Number,
      default: 1,
      required: false,
    },
    suffix: {
      type: String,
      default: '',
    },
    width: {
      type: String,
      default: '20rem',
    },
    valueMap: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    minTextValue() {
      return this.valueMap[this.min] || this.min;
    },
    maxTextValue() {
      return this.valueMap[this.max] || this.max;
    },
    textValue() {
      return this.valueMap[this.value] || this.value;
    },
    rangeStyles() {
      return {
        width: this.width,
        '--min': this.min,
        '--max': this.max,
        '--value': this.value,
        '--text-value': `'${this.textValue}'`,
        '--suffix': `'${this.suffix}'`,
        '--min-text-value': `'${this.minTextValue}'`,
        '--max-text-value': `'${this.maxTextValue}'`,
        '--value-background-hover': 'transparent',
        '--value-active-color': 'var(--text-color)',
      };
    },
  },

  methods: {
    rangeState: function () {
      if (!this.disabledCombined) {
        this.internalValue = !this.internalValue;
      }
    },
    onInput(evt) {
      const self = evt.target || evt.srcElement;
      const textValue = this.valueMap[self.value] || self.value;

      self.parentNode.style.setProperty('--value', self.value);
      self.parentNode.style.setProperty('--text-value', JSON.stringify(textValue));
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  @import '@yaireo/ui-range/ui-range.scss';
  @import '~styles/import';

  .hm-range {
    &--active {
      .hm-range {
        &__range {
          &__wrapper {
            background-color: $mint;
          }
          &__handle {
            left: calc(100% - 1.5rem);
            &:before, &:after {
              transform: rotate(0);
            }
          }
        }
      }
    }
    &--disabled {
      &.hm-range {
        cursor: not-allowed !important;
      }
      .hm-range {
        &__label {
          color: $gray;
        }
        &__range {
          pointer-events: none;
          &__wrapper {
            background-color: $white;
            border: 0.1rem solid $middlegray;
          }
        }
      }
    }
    &__wrapper {
      width: 100%;
      display: grid;
      grid-template-columns: 8fr 1fr;
      align-items: center;
      border-bottom: 0.1rem solid $middlegray;
      height: 3.8rem;
    }
    &__label {
      padding-left: 1rem;
    }
    &__help {
      color: $gray;
      font-size: 1.1rem;
      padding-left: 1rem;
      height: 1.4rem;
      line-height: 1.4rem;
      margin-bottom: 0.3rem;
      margin-top: 0.3rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .range-slider {
      --primary-color: #{$mint};
      --value-font: 500 1.2rem 'proxima-nova';
      --progress-background: #{$middlegray};
      --thumb-size: 1.5rem;
      --track-height: 1.7rem;
      --thumb-shadow: 0.1rem 0.1rem 0.2rem 0 #{rgba($black, 0.5)};
      --thumb-shadow-active: 0.1rem 0.1rem 0.2rem 0 #{rgba($black, 0.5)};
      --thumb-shadow-hover: 0.1rem 0.1rem 0.2rem 0 #{rgba($black, 0.5)};
      //--progress-shadow: 0.1rem 0.1rem 0.2rem 0 #{rgba($black, 0.5)};
      --thumb-transform: translateY(-0.1rem);
      --show-min-max: hidden;
      --min-max-font: 1.2rem 'proxima-nova';
      --ticks-color: #{$middlegray};
      --ticks-height: 0.0rem;
      --y-offset: 0.9rem;
      cursor: pointer!important;
      right: -0.5rem;

      &--disabled {
        --primary-color: #{$lightmiddlegray};
        --progress-background: #{$lightmiddlegray};
        --thumb-color: #{$middlegray};
        color: $gray;
        input {
          &::-webkit-slider-thumb {
            pointer-events: none!important;
          }
        }
      }

      &:before, &:after {
        --flip-y: 2.0rem;
      }

      &:before {
        content: var(--min-text-value);
      }

      &:after {
        content: var(--max-text-value);
      }

      & > input {
        width: calc(100% - 1.0rem);
        left: 0.5rem;
        cursor: pointer!important;

        &::-webkit-slider-runnable-track {
          background: transparent;
        }

        &:hover {
          & + output {
            --y-offset: 0.9rem;
            &:after {
              --value-font: 500 1.2rem 'proxima-nova';
            }
          }
        }

        &::-webkit-slider-thumb {
          &:before {
            content: ' ';
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmInputRange.vue